<script setup lang="ts">
import {
    computed, watch, onMounted, onUnmounted, ref, nextTick,
} from 'vue';
import { useDisplay } from 'vuetify';
import { BFilterBar, BSheet } from '@/design-system';
import { useFilterBar } from '@/composables';

type PropsType = {
    activeTab: string;
};

defineProps<PropsType>();

const { xs } = useDisplay();
const navigationDrawerWidth = ref(0);
const filterBarRef = ref<InstanceType<typeof BSheet> | null>(null);

const { observeFilterBarHeight } = useFilterBar();

let disconnectObserver: (() => void) | null = null;

const updateDrawerWidth = () => {
    const drawer = document.querySelector('.v-navigation-drawer');
    if (drawer && !xs.value) {
        navigationDrawerWidth.value = drawer.getBoundingClientRect().width;
    } else {
        navigationDrawerWidth.value = 0;
    }
};

onMounted(async () => {
    updateDrawerWidth();
    window.addEventListener('resize', updateDrawerWidth);
    await nextTick();

    if (filterBarRef.value) {
        const domElement = filterBarRef.value.$el || filterBarRef.value;

        if (domElement && domElement instanceof Element) {
            disconnectObserver = observeFilterBarHeight(domElement);
        }
    }
});

onUnmounted(() => {
    disconnectObserver?.();
    window.removeEventListener('resize', updateDrawerWidth);
});

watch(xs, () => {
    updateDrawerWidth();
});

const filterBarStyle = computed(() => ({ left: `${navigationDrawerWidth.value}px` }));
</script>

<template>
    <BSheet
        ref="filterBarRef"
        position="fixed"
        color="surface"
        elevation="8"
        class="b-fixed-filter-bar"
        :style="filterBarStyle"
    >
        <BFilterBar :active-tab="activeTab">
            <template #filters="{ activeTab: tab }">
                <slot
                    name="filters"
                    :active-tab="tab"
                />
            </template>
        </BFilterBar>
    </BSheet>
</template>

<style lang="scss" scoped>
.b-fixed-filter-bar {
    bottom: 0;
    right: 0;
    z-index: 100;
    box-shadow:
        0 -2px 8px rgba(0, 0, 0, 0.12),
        0 -4px 24px rgba(0, 0, 0, 0.08);
}
</style>
